import { faThumbsUp as faThumbsUpRegular } from "@fortawesome/pro-regular-svg-icons/faThumbsUp";
import { faThumbsUp as faThumbsUpSolid } from "@fortawesome/pro-solid-svg-icons/faThumbsUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, Typography } from "@mui/material";
import { MprcRequestIndicator } from "components";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { TrainingUserInteractionOption } from "generated/CoreApi_v1";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

///////////////////////////////////////////////////////////////////////////////

interface IProps {
    trainingId: string;
    option: TrainingUserInteractionOption.Like | TrainingUserInteractionOption.Unlike;
    onClose: (success: boolean) => void;
}

export const DoTrainingLike = ({ trainingId, option, onClose }: IProps) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");
    const [updating, setUpdating] = useState<boolean>(false);

    const doBookmark = () => {
        setUpdating(true);
        api.v1.lmsStudent.userInteraction({
            trainingId,
            option
        })
            .then((result) => {
                if (result.data === true) {
                    enqueueSnackbar(t(option == TrainingUserInteractionOption.Like
                        ? "Training.Like.LikeSuccessMessage"
                        : "Training.Like.RemoveLikeSuccessMessage"), { variant: "success" });
                }
                else {
                    enqueueSnackbar(t(option == TrainingUserInteractionOption.Unlike
                        ? "Training.Like.LikeErrorMessage"
                        : "Training.Like.RemoveLikeErrorMessage"), { variant: "error" });
                }

                onClose(result.data);
            })
            .catch(MprcRequestSnackbarErrorReport)
            .finally(() => setUpdating(false));
    };

    useEffect(() => {
        doBookmark();
    }, []);

    return <>{updating ? <MprcRequestIndicator visible={updating} type="post" /> : <></>}</>;
};

///////////////////////////////////////////////////////////////////////////////

interface ILikeButtonProps {
    trainingId: string;
    liked?: boolean;
    readonly: boolean;
    count: number;
    onChange: (liked: boolean, count: number) => void;
}

///
export const TrainingLikeButton = ({ trainingId, readonly, count, liked = false, onChange }: ILikeButtonProps) => {
    const api = useCoreApi();
    const [updating, setUpdating] = useState<boolean>(false);
    const { t } = useTranslation("lms");

    const addOrRemoveLike = () => {
        setUpdating(true);
        api.v1.lmsStudent.userInteraction({
            trainingId,
            option: liked ? TrainingUserInteractionOption.Unlike : TrainingUserInteractionOption.Like
        })
            .then((result) => {
                if (result.data) {
                    if (onChange) onChange(!liked, !liked ? count + 1 : count - 1);
                }
            })
            .catch(MprcRequestSnackbarErrorReport)
            .finally(() => setUpdating(false));
    };

    return (
        <Typography
            variant="iconText"
            color={liked ? "highlight.main" : "muted.main"}
            title={liked ? t("Training.Like.LikedTooltip") : t("Training.Like.LikeTooltip")}
            component="div"
        >
            <IconButton color="inherit" onClick={addOrRemoveLike} disabled={updating || readonly}>
                <FontAwesomeIcon icon={liked ? faThumbsUpSolid : faThumbsUpRegular} fixedWidth />
            </IconButton>
            <span>{count}</span>
        </Typography>
    );
};

///////////////////////////////////////////////////////////////////////////////

interface ITrainingLikeCardButtonProps {
    trainingId: string;
    liked?: boolean;
    readonly: boolean;
    onChange?: (liked: boolean) => void;
}

///
export const TrainingLikeCardButton = ({ trainingId, readonly, liked = false, onChange }: ITrainingLikeCardButtonProps) => {
    const api = useCoreApi();
    const [updating, setUpdating] = useState<boolean>(false);
    const { t } = useTranslation("lms");

    const addOrRemoveLike = () => {
        setUpdating(true);
        api.v1.lmsStudent.userInteraction({
            trainingId,
            option: liked ? TrainingUserInteractionOption.Unlike : TrainingUserInteractionOption.Like
        })
            .then((result) => {
                if (result.data) {
                    if (onChange) onChange(!liked);
                }
            })
            .catch(MprcRequestSnackbarErrorReport)
            .finally(() => setUpdating(false));
    };

    return (
        <Typography
            variant="iconText"
            color={liked ? "highlight.main" : "muted.main"}
            title={liked ? t("Training.Like.LikedTooltip") : t("Training.Like.LikeTooltip")}
            component="div">
            <IconButton color="inherit" onClick={addOrRemoveLike} disabled={updating || readonly}>
                <FontAwesomeIcon icon={liked ? faThumbsUpSolid : faThumbsUpRegular} fixedWidth />
            </IconButton>
        </Typography>
    );
};
